import React, { useState, useEffect, useContext } from "react";
import { ref, onValue, update, remove, push, set } from "firebase/database";
import { database } from "./firebaseConfig";
import { UserContext } from "./UserContext";
import { useNavigate } from "react-router-dom";
import {
  ThumbsUp,
  List,
  Upload,
  Lock,
  Unlock,
  Trash2,
  Search,
  Share2,
} from "lucide-react";

// Import UI components
import { Button } from "./components/ui/button";
import { ScrollArea } from "./components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Switch } from "./components/ui/switch";
import Header from "./components/header";
import Loader from "./components/loader";
import PublicListing from "./components/public-listing";

// New UI components for list view
const ViewListButton = ({ onClick, children }) => (
  <Button
    variant="outline"
    className="border-gray-300 text-gray-300 hover:bg-gray-700"
    onClick={onClick}
  >
    {children}
  </Button>
);

const ViewListScroll = ({ children }) => (
  <ScrollArea className="h-[300px]">{children}</ScrollArea>
);

export default function Dashboard() {
  const { currentUser, users } = useContext(UserContext);
  const navigate = useNavigate();
  const [lists, setLists] = useState({});
  const [email, setEmail] = useState("");
  const [selectedList, setSelectedList] = useState(null);

  const [uploadFile, setUploadFile] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!currentUser) return;

    setIsLoading(true);
    setError(null);

    // Fetch lists
    const listsRef = ref(database, "lists");
    const unsubscribeLists = onValue(
      listsRef,
      (snapshot) => {
        const data = snapshot.val();
        setLists(data || {});
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching lists:", error);
        setError("Failed to load lists. Please try again later.");
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribeLists();
    };
  }, [currentUser]);

  if (!currentUser) {
    return (
      <p className="text-center text-gray-100 mt-8">
        Please log in to view this page.
      </p>
    );
  }

  if (isLoading) {
    return <Loader isFullScreen />;
  }

  if (error) {
    return <p className="text-center text-gray-100 mt-8">Error: {error}</p>;
  }

  const userLists = Object.entries(lists || {}).filter(
    ([_, list]) =>
      list &&
      (list.createdBy === currentUser.uid ||
        list.sharedWith?.includes(currentUser.email))
  );
  const likedLists = Object.entries(lists || {}).filter(
    ([_, list]) => list && list.likes && list.likes[currentUser.uid]
  );
  const publicLists = Object.entries(lists || {}).filter(
    ([_, list]) => list && list.privacy === "public"
  );

  const filteredPublicLists = publicLists.filter(
    ([_, list]) =>
      list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (users[list.createdBy]?.displayName || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const toggleListPrivacy = (listId) => {
    if (!lists[listId]) return;
    const listRef = ref(database, `lists/${listId}`);
    const newPrivacy =
      lists[listId].privacy === "public" ? "private" : "public";
    update(listRef, { privacy: newPrivacy }).catch((error) => {
      console.error("Error toggling list privacy:", error);
      alert("Failed to update list privacy. Please try again.");
    });
  };

  const handleDeleteList = (listId) => {
    const listRef = ref(database, `lists/${listId}`);
    remove(listRef).catch((error) => {
      console.error("Error deleting list:", error);
      alert("Failed to delete list. Please try again.");
    });
  };

  const handleUnlikeList = (listId) => {
    const likesRef = ref(database, `lists/${listId}/likes/${currentUser.uid}`);
    remove(likesRef).catch((error) => {
      console.error("Error unliking list:", error);
      alert("Failed to unlike list. Please try again.");
    });
  };

  const handleUpload = () => {
    if (uploadFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const fileName = uploadFile.name;
        const fileExtension = fileName.split(".").pop().toLowerCase();
        
        let newList = {
          name: uploadFile.name.replace(/\.[^/.]+$/, ""),
          createdBy: currentUser.uid,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          privacy: "private",
          sharedWith: [],
          likes: {},
          history: {},
        };

        if (fileExtension === "csv") {
          // Process CSV file
          const lines = content.split("\n");
          const headers = lines[0].split(",").map(header => header.trim());
          const rows = lines.slice(1).map(line => {
            const values = line.split(",");
            return Object.fromEntries(
              headers.map((header, index) => [
                header,
                values[index] ? values[index].trim() : ""
              ])
            );
          });

          newList = {
            ...newList,
            fileType: "csv",
            data: rows.reduce((acc, row, index) => {
              acc[index] = row;
              return acc;
            }, {})
          };
        } else {
          // Process text file
          newList = {
            ...newList,
            fileType: "text",
            textData: content
          };
        }

        const listsRef = ref(database, "lists");
        push(listsRef, newList)
          .then(() => {
            setUploadFile(null);
            setIsUploadDialogOpen(false);
            alert("List uploaded successfully!");
          })
          .catch((error) => {
            console.error("Error uploading list:", error);
            alert("Failed to upload list. Please try again.");
          });
      };
      reader.readAsText(uploadFile);
    }
  };

  const handleViewList = (listId) => {
    navigate(`/list/${listId}`);
  };

  const handleShareList = () => {
    const [userId] = Object.entries(users).find(
      ([_, user]) => user.email === email
    ) || [null, null];

    if (!userId) {
      alert("User with this email does not exist.");
      return;
    }

    if (email) {
      const listRef = ref(database, `lists/${selectedList}/sharedWith`);
      const sharedWith = lists[selectedList].sharedWith || [];
      if (sharedWith.includes(email)) {
        alert("This list is already shared with this user.");
        return;
      }
      sharedWith.push(email);
      set(listRef, sharedWith)
        .then(() => {
          setIsShareDialogOpen(false);
          alert("List shared successfully!");
        })
        .catch((error) => {
          console.error("Error sharing list:", error);
          alert("Failed to share list. Please try again.");
        });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-semibold text-green-400">
            Your Dashboard
          </h2>
          <Dialog
            open={isUploadDialogOpen}
            onOpenChange={setIsUploadDialogOpen}
          >
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                <Upload className="mr-2 h-4 w-4" /> Upload List
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-800 text-gray-100">
              <DialogHeader>
                <DialogTitle className="text-green-400">
                  Upload a New List
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="list-file" className="text-right">
                    File
                  </Label>
                  <Input
                    id="list-file"
                    type="file"
                    accept=".csv,.txt,.md"
                    className="col-span-3 bg-gray-700 text-gray-100 border-gray-600"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleUpload}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Upload
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
            <DialogContent className="bg-gray-800 text-gray-100">
              <DialogHeader>
                <DialogTitle className="text-green-400">
                  Share the List
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="user-email" className="text-right">
                    Email
                  </Label>
                  <Input
                    id="user-email"
                    type="email"
                    className="col-span-3 bg-gray-700 text-gray-100 border-gray-600"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => handleShareList()}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Share
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-semibold mb-4 text-green-400 flex items-center">
                <List className="mr-2" /> Your Lists
              </h2>
              <ViewListScroll>
                <ul className="space-y-4">
                  {userLists.map(([listId, list]) => (
                    <li
                      key={listId}
                      className="bg-gray-900 rounded-lg p-4 transition-all hover:shadow-lg"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-300">
                          {list.name}
                        </h3>
                        {list.createdBy === currentUser.uid && (
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={list.privacy === "public"}
                              onCheckedChange={() => toggleListPrivacy(listId)}
                              className="data-[state=checked]:bg-green-500"
                            />
                            {list.privacy === "public" ? (
                              <Unlock className="h-4 w-4 text-green-500" />
                            ) : (
                              <Lock className="h-4 w-4 text-gray-500" />
                            )}
                          </div>
                        )}
                      </div>
                      {list.createdBy !== currentUser.uid && (
                        <div>
                          <p className="text-gray-400 my-2">
                            Shared by:{" "}
                            {users[list.createdBy]?.displayName ||
                              "Unknown User"}
                          </p>
                        </div>
                      )}
                      <p className="text-gray-400 mb-2">
                        {Object.keys(list.data || {}).length} items
                      </p>
                      <div className="flex justify-between items-center">
                        <ViewListButton onClick={() => handleViewList(listId)}>
                          View List
                        </ViewListButton>
                        {list.createdBy === currentUser.uid && (
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="border-gray-300 text-gray-300 hover:bg-gray-700 p-2"
                              onClick={() => {
                                setSelectedList(listId);
                                setIsShareDialogOpen(true);
                              }}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={() => handleDeleteList(listId)}
                              className="bg-red-600 hover:bg-red-700 p-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </ViewListScroll>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-semibold mb-4 text-green-400 flex items-center">
                <ThumbsUp className="mr-2" /> Liked Lists
              </h2>
              <ScrollArea className="h-[300px]">
                <ul className="space-y-4">
                  {likedLists.map(([listId, list]) => (
                    <li
                      key={listId}
                      className="bg-gray-900 rounded-lg p-4 transition-all hover:shadow-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-300">
                        {list.name}
                      </h3>
                      <p className="text-gray-400">
                        {Object.keys(list.data || {}).length} items
                      </p>
                      <p className="text-gray-400">
                        Created by:{" "}
                        {users[list.createdBy]?.displayName || "Unknown User"}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <Button
                          variant="outline"
                          className="border-gray-300 text-gray-300 hover:bg-gray-700"
                          onClick={() => handleViewList(listId)}
                        >
                          View List
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleUnlikeList(listId)}
                          className="border-gray-300 text-gray-300 hover:bg-gray-700 p-2"
                        >
                          <ThumbsUp className="h-4 w-4 fill-current" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-6">
            <h2 className="text-2xl font-semibold mb-4 text-green-400 flex items-center">
              <Search className="mr-2" /> Discover Public Lists
            </h2>
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Search public lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-700 text-gray-100 border-gray-600"
              />
            </div>
            <ScrollArea className="h-[500px]">
              <ul className="space-y-4">
                {filteredPublicLists.map(([listId, list]) => (
                  <PublicListing key={listId} listId={listId} list={list} />
                ))}
              </ul>
            </ScrollArea>
          </div>
        </div>
      </main>
    </div>
  );
}
